import { useState, useCallback, useEffect } from 'react';
import { executeQueryV2 } from '../../../database-objects/services/queryExecutor';
import { wrapWithLimit } from '../../common/utils/peekResultsDetection';
import { createExecutionId } from '../../../../types/queryExecution';
import type { PeekResultsState } from '../components/PeekResultsPanel';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { DatabaseEngineId } from '../../../../core/engines';

const PEEK_MAX_ROWS = 50;
const PEEK_TIMEOUT_MS = 10000;

interface UsePeekResultsOptions {
  editorRef: React.RefObject<SqlMonacoHandle | null>;
  connectionId?: string;
  database?: string;
  engineId: DatabaseEngineId;
  tabId: string;
}

/**
 * Manages peek query execution and state. The panel is rendered as an absolute
 * overlay *over* the editor container (not inside a Monaco view zone), so it
 * does not push editor content down or add an editor scrollbar. Positioning is
 * handled by the `PeekResultsOverlay` component that reads the returned
 * `peekState` + `peekQuery`.
 */
/** Line number the panel is anchored to (captured when Peek is clicked). */
export type PeekAnchor = { lineNumber: number; column: number };

export function usePeekResults({
  editorRef,
  connectionId,
  database,
  engineId,
  tabId,
}: UsePeekResultsOptions) {
  const [peekState, setPeekState] = useState<PeekResultsState | null>(null);
  const [peekQuery, setPeekQuery] = useState<string>('');
  const [peekAnchor, setPeekAnchor] = useState<PeekAnchor | null>(null);
  const isPeekExecuting = peekState?.status === 'loading';

  const closePeek = useCallback(() => {
    setPeekState(null);
    setPeekQuery('');
    setPeekAnchor(null);
  }, []);

  const handlePeek = useCallback(async (selectedSql: string, anchorLine?: number) => {
    if (!connectionId || isPeekExecuting) return;

    const editor = editorRef.current;
    if (!editor) return;

    // Capture the line at the END of the peeked SQL so the panel anchors to
    // the bottom of the statement. Callers that have an explicit anchor
    // (e.g., the cursor-statement bar, which has the statement's range) pass
    // it directly; otherwise we fall back to the editor's current selection
    // end line (used by the selection floating bar). If neither is
    // available the panel won't render — `PeekResultsOverlay` needs a line
    // to pixel-anchor against.
    const resolvedAnchorLine = anchorLine ?? editor.getSelectionEndLine() ?? undefined;
    setPeekAnchor(resolvedAnchorLine != null ? { lineNumber: resolvedAnchorLine, column: 1 } : null);

    setPeekQuery(selectedSql);
    setPeekState({ status: 'loading' });

    // The row-limit wrapper is a UX nicety, not a safety gate. The safety gate is
    // isExecutableSelect() in peekResultsDetection.ts, which only allows read-only
    // statements (SELECT, WITH, TABLE, VALUES, etc.) to reach this point.
    const wrappedSql = wrapWithLimit(selectedSql, engineId, PEEK_MAX_ROWS);
    const executionId = createExecutionId(`peek-${tabId}`);

    const result = await executeQueryV2({
      connectionId,
      sql: wrappedSql,
      database,
      executionId,
      tabId,
      options: {
        maxRows: PEEK_MAX_ROWS,
        timeoutMs: PEEK_TIMEOUT_MS,
      },
    });

    // If the panel was closed while the query was in flight, drop the result.
    setPeekState((current) => {
      if (current === null) return null;
      if (result.success && result.resultSets?.[0]) {
        const resultSet = result.resultSets[0];
        // The peek wraps the user's SQL with a LIMIT/TOP/FETCH clause via
        // `wrapWithLimit` so we never pull more than `PEEK_MAX_ROWS` rows.
        // Treat as capped when either the engine reports truncation OR we
        // received exactly the cap's worth of rows (almost always means the
        // real query has more rows the user can't see in the peek).
        const engineTruncated = result.stats?.truncated === true;
        const hitWrapperLimit = resultSet.rows.length >= PEEK_MAX_ROWS;
        const isCapped = engineTruncated || hitWrapperLimit;
        return {
          status: 'success',
          resultSet,
          query: selectedSql,
          isCapped,
          cappedRowLimit: PEEK_MAX_ROWS,
        };
      }
      return { status: 'error', message: result.error?.message || 'Query execution failed' };
    });
  }, [connectionId, isPeekExecuting, editorRef, engineId, tabId, database]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setPeekState(null);
    };
  }, []);

  return { isPeekExecuting, handlePeek, closePeek, peekState, peekQuery, peekAnchor };
}
